import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { KineticHeading } from "@/components/common/Reveal";
import { useQuote } from "@/context/QuoteContext";
import { COMPANY } from "@/data/site";

export default function Hero() {
  const ref = useRef(null);
  const { openQuote } = useQuote();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-gema-black"
    >
      {/* Background image with parallax — replace with cinematic video later */}
      <motion.div className="absolute inset-0 z-0" style={{ y, scale }}>
        <img
          src="https://images.unsplash.com/photo-1606577924006-27d39b132ae2"
          alt="Motor de alto desempeño en detalle"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <motion.div className="absolute inset-0 z-10 bg-black" style={{ opacity: overlayOpacity }} />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-gema-black via-black/30 to-black/60" />

      <div className="container-gema relative z-20 pb-20 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="overline mb-6"
        >
          {COMPANY.tagline}
        </motion.div>

        <KineticHeading
          className="heading-xl text-white max-w-5xl"
          lines={["Mantenimiento,", "Reparación y", "Suministro Técnico"]}
          accentIndex={2}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed"
        >
          Soluciones integrales para los sectores automotriz, industrial y naval. Experiencia técnica,
          repuestos certificados y atención profesional que impulsan el óptimo desempeño de tus equipos.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button data-testid="hero-quote-btn" onClick={() => openQuote()} className="btn-primary group">
            Solicitar Cotización
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="btn-outline-light">
            <MessageCircle className="h-4 w-4" /> Contactar por WhatsApp
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}
